import type { ChatMessageGroup } from '@provision/chat-utils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import {
  useChatPagination,
  useMergedTimeline,
} from 'src/pages/Conversations/Chat/registry/hooks';
import { MessageTypeId, TimelineEntry } from 'src/pages/Conversations/Chat/registry/types';
import { useNotifications } from 'src/utils/hooks';
import {
  DeepLinkPhase,
  MessageDeepLinkContext,
  MessageDeepLinkState,
} from './message-deep-link-context';
import { useCenteredRowAnchor } from './use-centered-row-anchor';
import { useMessageDeepLinkParam } from './use-message-deep-link-param';
import {
  TargetLookupStatus,
  TargetMessageLookup,
  useTargetMessageLookup,
} from './use-target-message-lookup';
import { TargetReplyStatus, useTargetReplyThread } from './use-target-reply-thread';

// A safety stop for a pagination signal that never turns false, not a functional limit:
// the target's createdAt ends the paging long before this in practice.
export const MAX_DEEP_LINK_PAGES = 100;

interface PageRequests {
  targetMessageId: string;
  count: number;
  requestedWith: () => Promise<void>;
}

interface RenderedMessages {
  ids: Set<string>;
  oldestMs: number;
  newestMs: number;
}

enum NewestPageReloadStatus {
  Pending = 'pending',
  HasTarget = 'has-target',
  LacksTarget = 'lacks-target',
}

interface NewestPageReload {
  targetMessageId: string;
  status: NewestPageReloadStatus;
  renderedBefore: RenderedMessages;
}

function collectRenderedMessages(entries: TimelineEntry<unknown>[]): RenderedMessages {
  const ids = new Set<string>();
  let oldestMs = Infinity;
  let newestMs = -Infinity;
  entries.forEach((entry) => {
    if (entry.type !== MessageTypeId.ChatMessage) return;
    (entry.item as ChatMessageGroup<unknown>).messages.forEach((message) => {
      ids.add(message.id);
      const ms = Date.parse(message.createdAt);
      if (ms < oldestMs) oldestMs = ms;
      if (ms > newestMs) newestMs = ms;
    });
  });
  return { ids, oldestMs, newestMs };
}

interface PhaseInput {
  targetMessageId: string | null;
  lookup: TargetMessageLookup;
  rendered: RenderedMessages;
  hasMore: boolean;
  isPaging: boolean;
  pagesRequested: number;
  isTargetNewerThanWindow: boolean;
  newestPageReload: NewestPageReload | null;
  replyStatus: TargetReplyStatus;
}

function resolvePhase({
  targetMessageId,
  lookup,
  rendered,
  hasMore,
  isPaging,
  pagesRequested,
  isTargetNewerThanWindow,
  newestPageReload,
  replyStatus,
}: PhaseInput): DeepLinkPhase {
  if (!targetMessageId) return DeepLinkPhase.Idle;
  if (rendered.ids.has(targetMessageId)) return DeepLinkPhase.Found;
  if (lookup.status === TargetLookupStatus.Missing) return DeepLinkPhase.NotFound;
  // before the first page lands `hasMore` reads false (an "unfilled" page of zero records)
  if (lookup.status !== TargetLookupStatus.Found || isPaging || rendered.ids.size === 0) {
    return DeepLinkPhase.Locating;
  }

  if (lookup.timelineMessageId && rendered.ids.has(lookup.timelineMessageId)) {
    return resolveReplyPhase(replyStatus);
  }

  if (isTargetNewerThanWindow) {
    return resolveNewerTargetPhase(newestPageReload, rendered);
  }

  /*
    The loaded window already reaches past the target, yet it is not on screen: the row is
    hidden (an SMS session folds its messages away), so no further page can bring it.
  */
  const isWindowPastTarget = rendered.oldestMs < Date.parse(lookup.createdAt ?? '');
  if (!hasMore || isWindowPastTarget || pagesRequested >= MAX_DEEP_LINK_PAGES) {
    return DeepLinkPhase.NotFound;
  }
  return DeepLinkPhase.Locating;
}

// the target reply's note is on screen: the reply is either in its thread or out of reach
function resolveReplyPhase(replyStatus: TargetReplyStatus): DeepLinkPhase {
  switch (replyStatus) {
    case TargetReplyStatus.Shown:
      return DeepLinkPhase.Found;
    case TargetReplyStatus.Refreshing:
      return DeepLinkPhase.Locating;
    default:
      return DeepLinkPhase.NotFound;
  }
}

/*
  A target newer than every loaded message arrived after the thread was cached (a
  notification about a fresh message), so no page further back can bring it — only a
  reload of the newest page can. The reload's own result decides, not the next render:
  the reload settles on the cache write, before the thread re-renders with the page.
*/
function resolveNewerTargetPhase(
  reload: NewestPageReload | null,
  rendered: RenderedMessages,
): DeepLinkPhase {
  switch (reload?.status) {
    case NewestPageReloadStatus.LacksTarget:
      return DeepLinkPhase.NotFound;
    case NewestPageReloadStatus.HasTarget:
      // the page re-rendered without the row: it is folded away, like an SMS session's
      return rendered === reload.renderedBefore
        ? DeepLinkPhase.Locating
        : DeepLinkPhase.NotFound;
    default:
      return DeepLinkPhase.Locating;
  }
}

/*
  Brings the message named by `?messageId` (for a reply, its note) into the thread, centres
  it and highlights it
  until the reader's first gesture. Mounted per conversation (Chat is keyed by it), so
  switching conversations starts from a clean state.
*/
export const MessageDeepLinkProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const conversationId = useChatCommonData((s) => s.conversationId);
  const chatRef = useChatCommonData((s) => s.chatRef);
  const gestureClock = useChatCommonData((s) => s.gestureClock);
  const scrollGuard = useChatCommonData((s) => s.scrollGuard);
  const { showErrorAlert } = useNotifications();
  const anchorRow = useCenteredRowAnchor(chatRef, scrollGuard, gestureClock);

  const { targetMessageId, clearTargetParam } = useMessageDeepLinkParam();
  const lookup = useTargetMessageLookup(conversationId, targetMessageId);
  const entries = useMergedTimeline();
  const { hasMore, isLoadingMore, loadMore, reloadNewestPage } = useChatPagination();

  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [pageRequests, setPageRequests] = useState<PageRequests>();
  const [newestPageReloadRequest, setNewestPageReloadRequest] = useState<NewestPageReload>();
  const newestPageReload =
    newestPageReloadRequest?.targetMessageId === targetMessageId
      ? newestPageReloadRequest
      : null;
  const isCurrentTarget = pageRequests?.targetMessageId === targetMessageId;
  const pagesRequested = isCurrentTarget ? pageRequests.count : 0;

  /*
    `loadMore` closes over the loaded offset, so it is replaced exactly when a requested
    page has merged into the thread. Until then the previous request is still in flight:
    its promise settles on the cache write, before the thread re-renders with the page, and
    requesting again at that point would load one page past the target.
  */
  const isAwaitingMerge = isCurrentTarget && pageRequests.requestedWith === loadMore;

  const rendered = useMemo(() => collectRenderedMessages(entries), [entries]);
  const { timelineMessageId } = lookup;
  const isTimelineMessageRendered = !!timelineMessageId && rendered.ids.has(timelineMessageId);
  const replyStatus = useTargetReplyThread({
    targetMessageId,
    timelineMessageId,
    isTimelineMessageRendered,
  });

  const isPaging = isLoadingMore || isAwaitingMerge;
  const isTargetNewerThanWindow = Date.parse(lookup.createdAt ?? '') > rendered.newestMs;
  const phase = resolvePhase({
    targetMessageId,
    lookup,
    rendered,
    hasMore,
    isPaging,
    pagesRequested,
    isTargetNewerThanWindow,
    newestPageReload,
    replyStatus,
  });

  // the first page has to be on screen before paging: until then loadMore is a no-op
  const canFetchTowardsTarget =
    phase === DeepLinkPhase.Locating &&
    lookup.status === TargetLookupStatus.Found &&
    !isPaging &&
    rendered.ids.size > 0 &&
    !isTimelineMessageRendered;
  const shouldRequestPage = canFetchTowardsTarget && !isTargetNewerThanWindow;
  const shouldReloadNewestPage =
    canFetchTowardsTarget && isTargetNewerThanWindow && !newestPageReload;

  /*
    Deliberately ungated by the scroll gesture gate: this chain is the one place the thread
    pages without a reader gesture, bounded by the target's createdAt and the page cap.
  */
  useEffect(() => {
    if (!shouldRequestPage || !targetMessageId) return;
    setPageRequests((prev) => ({
      targetMessageId,
      count: prev?.targetMessageId === targetMessageId ? prev.count + 1 : 1,
      requestedWith: loadMore,
    }));
    void loadMore();
  }, [shouldRequestPage, targetMessageId, loadMore]);

  useEffect(() => {
    if (!shouldReloadNewestPage || !targetMessageId) return;
    setNewestPageReloadRequest({
      targetMessageId,
      status: NewestPageReloadStatus.Pending,
      renderedBefore: rendered,
    });

    const reload = async () => {
      let status = NewestPageReloadStatus.LacksTarget;
      try {
        const addedIds = await reloadNewestPage();
        if (timelineMessageId && addedIds.includes(timelineMessageId)) {
          status = NewestPageReloadStatus.HasTarget;
        }
      } catch {
        // a failed reload leaves the target out of reach, which the not-found toast reports
      }
      setNewestPageReloadRequest((prev) =>
        prev?.targetMessageId === targetMessageId ? { ...prev, status } : prev,
      );
    };
    void reload();
  }, [shouldReloadNewestPage, targetMessageId, timelineMessageId, reloadNewestPage, rendered]);

  /*
    A ref, not state: it only stops a resolved target from being acted on twice while the
    param removal is still propagating through the router, and must not re-render.
  */
  const resolvedTargetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!targetMessageId) {
      resolvedTargetRef.current = null;
      return;
    }
    if (resolvedTargetRef.current === targetMessageId) return;

    if (phase === DeepLinkPhase.Found) {
      resolvedTargetRef.current = targetMessageId;
      anchorRow(targetMessageId);
      setHighlightedMessageId(targetMessageId);
      clearTargetParam();
    } else if (phase === DeepLinkPhase.NotFound) {
      resolvedTargetRef.current = targetMessageId;
      showErrorAlert('Message not found in this conversation');
      clearTargetParam();
    }
  }, [phase, targetMessageId, anchorRow, clearTargetParam, showErrorAlert]);

  const clearHighlight = useCallback(() => setHighlightedMessageId(null), []);

  useEffect(() => {
    if (!highlightedMessageId) return;

    const unsubscribe = gestureClock.subscribe(clearHighlight);
    // capture: a click that a child stops from bubbling still counts as interaction
    document.addEventListener('click', clearHighlight, true);
    return () => {
      unsubscribe();
      document.removeEventListener('click', clearHighlight, true);
    };
  }, [highlightedMessageId, gestureClock, clearHighlight]);

  const value = useMemo<MessageDeepLinkState>(
    () => ({ targetMessageId, phase, highlightedMessageId }),
    [targetMessageId, phase, highlightedMessageId],
  );

  return (
    <MessageDeepLinkContext.Provider value={value}>{children}</MessageDeepLinkContext.Provider>
  );
};
